import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Panel } from "@/components/Widgets";
import { errMsg, http } from "@/lib/api";

const csv = (a) => (a || []).join(", ");
const split = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

export default function MappingConfig() {
  const [cfg, setCfg] = useState(null);
  const [aliases, setAliases] = useState({});
  const [ignore, setIgnore] = useState("");
  useEffect(() => {
    http.get("/mapping").then((r) => {
      setCfg(r.data);
      setAliases(Object.fromEntries(Object.entries(r.data.section_aliases).map(([k, v]) => [k, csv(v)])));
      setIgnore(csv(r.data.ignore_sheets));
    }).catch((e) => toast.error(errMsg(e)));
  }, []);
  if (!cfg) return null;
  const setRow = (i, k, v) => setCfg((c) => ({ ...c, sheet_map: c.sheet_map.map((m, j) => (j === i ? { ...m, [k]: v } : m)) }));
  const save = async () => {
    try {
      await http.put("/mapping", {
        sheet_map: cfg.sheet_map, ignore_sheets: split(ignore),
        section_aliases: Object.fromEntries(Object.entries(aliases).map(([k, v]) => [k, split(v)])),
      });
      toast.success("Mapping rules saved — applied to the next upload");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-6" data-testid="mapping-config">
      <Panel title="Worksheet → entity mapping" subtitle={`${cfg.known_items_count} confirmed line items on record · overrides: ${Object.keys(cfg.line_overrides).length}`}
        actions={<Button onClick={save} className="bg-amber-500 text-slate-950 hover:bg-amber-400" data-testid="mapping-save-button">Save rules</Button>}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
              <th className="py-2 pr-2">Sheet</th><th className="py-2 pr-2">Display name</th><th className="py-2 pr-2">Short</th><th className="py-2 pr-2">Category</th><th className="py-2 pr-2">NCF row label</th><th className="py-2">Include</th>
            </tr></thead>
            <tbody>
              {cfg.sheet_map.map((m, i) => (
                <tr key={m.sheet} className="border-t border-slate-800" data-testid={`mapping-row-${m.code}`}>
                  <td className="num py-2 pr-2 text-xs text-amber-400">{m.sheet}</td>
                  {["name", "short", "category", "ncf_label"].map((k) => (
                    <td key={k} className="py-2 pr-2">
                      <Input value={m[k] || ""} onChange={(e) => setRow(i, k, e.target.value)} className="h-8 border-slate-700 bg-slate-900/70 text-xs" data-testid={`mapping-${m.code}-${k}-input`} />
                    </td>
                  ))}
                  <td className="py-2"><Switch checked={m.include !== false} onCheckedChange={(v) => setRow(i, "include", v)} data-testid={`mapping-${m.code}-include-switch`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
      <Panel title="Section label aliases" subtitle="Rows whose column-A label matches one of these start a section (comma separated, case-insensitive)">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {Object.keys(aliases).map((k) => (
            <label key={k} className="text-xs text-slate-400">
              <span className="mb-1 block">{cfg.section_labels[k] || k}</span>
              <Input value={aliases[k]} onChange={(e) => setAliases((a) => ({ ...a, [k]: e.target.value }))} className="h-8 border-slate-700 bg-slate-900/70 text-xs" data-testid={`alias-${k}-input`} />
            </label>
          ))}
          <label className="text-xs text-slate-400">
            <span className="mb-1 block">Ignored worksheets</span>
            <Input value={ignore} onChange={(e) => setIgnore(e.target.value)} className="h-8 border-slate-700 bg-slate-900/70 text-xs" data-testid="ignore-sheets-input" />
          </label>
        </div>
      </Panel>
    </div>
  );
}
